import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSupplierInvoices, useReadyToInvoice } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { money } from "@/lib/format";
import { toast } from "sonner";
import {
  FileText, ShieldCheck, Truck, FilePlus, Eye, UploadCloud, Send, Banknote,
  Loader2, Check, AlertTriangle, MessageCircle, ScrollText, Gavel, Mail, Phone,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Finance-only Customer Profile sections (Aug 2026 Finance scope pass,
// round 3). Every one of these is mounted from Customer360.jsx strictly
// behind `role === "finance"` — none of it renders for Sales/Operations/
// Management, and nothing here duplicates data or invents a parallel
// approval/invoice system. Every write action below calls a real,
// already-existing backend endpoint:
//   - Final invoice: POST /finance/final-invoice/draft + /approve (same
//     calls Receivables.jsx / Pipeline.jsx already make)
//   - Upload: POST /documents/upload (same generic endpoint AutoFileDialog
//     already uses everywhere else in this app)
//   - Cost variance: POST /finance/supplier-invoices/{id}/request-variance-
//     approval (creates a real APR-xxxx row in the SAME `approvals`
//     collection/decide() flow the existing "Approvals for this lead"
//     section on this page already renders/decides — no second system)
//   - Payment: POST /finance/customer-invoices/{id}/payment (same call
//     Pipeline.jsx's Payment Pending dialog used before this round)
// ---------------------------------------------------------------------------

const statusKey = (value) => String(value || "").trim().toUpperCase().replaceAll("_", " ");
const outstandingFor = (invoice) => Number(
  invoice?.outstandingAmount ?? invoice?.outstanding ?? invoice?.balance ??
  Math.max(Number(invoice?.amount || 0) - Number(invoice?.paidAmount || 0), 0),
);
const today = () => new Date().toISOString().slice(0, 10);

// ============================== 1. DOCUMENTS ==============================
// Classification is derived entirely from fields the backend already sets
// at upload time (`category`: TRADE / COST_EVIDENCE / GENERAL, and `type`)
// — nothing new is stored. Where a document truly has no reliable signal
// either way it falls into Trade/Shipment (the broadest, least-consequential
// bucket) rather than being silently dropped from every section.
const KYC_TYPES = new Set([
  "Aadhaar Card", "PAN Card", "CIN Certificate", "VAT / TRN Certificate", "Trade Licence",
  "KYC Form", "Authorised Signatory ID", "Credit Application",
]);
const TRADE_TYPES = new Set([
  "Commercial Invoice", "Packing List", "Bill of Lading", "House AWB", "Master AWB", "HAWB", "MAWB",
  "Certificate of Origin", "MSDS", "Customs Declaration", "Delivery Order", "POD", "Insurance Certificate",
]);
const QUOTATION_TYPES = new Set(["Quotation", "Customer Quotation", "Vendor Quotation", "Booking Confirmation"]);

function classifyDoc(d) {
  if (d.category === "COST_EVIDENCE") return "ops";
  if (QUOTATION_TYPES.has(d.type)) return "quotation";
  if (KYC_TYPES.has(d.type)) return "kyc";
  return "trade";
}

function DocRow({ d, onOpenDoc }) {
  return (
    <button onClick={() => onOpenDoc(d)} data-testid={`fin-doc-${d.id}`} className="w-full text-left py-2 flex items-center gap-2.5 hover:bg-slate-50 rounded-lg px-1">
      <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-slate-800 truncate">{d.name}</div>
        <div className="text-[11px] text-slate-400">{d.type || "Document"} · Submitted {d.date || "—"} · By {d.uploadedBy || d.source || "—"}</div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <StatusPill status={d.review || "Pending review"} />
        <Eye className="h-3.5 w-3.5 text-slate-300" />
      </div>
    </button>
  );
}

function DocGroup({ title, icon: Icon, docs, onOpenDoc, testid, hint, children }) {
  return (
    <div className="pt-3 border-t border-slate-100 first:border-t-0 first:pt-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5 text-indigo-500" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title} · {docs.length}</span>
      </div>
      {hint && <div className="text-[11px] text-slate-400 mb-1.5">{hint}</div>}
      {children}
      {docs.length === 0 ? (
        <div className="text-[12px] text-slate-400 pb-1.5">None on file.</div>
      ) : (
        <div className="divide-y divide-slate-100">{docs.map((d) => <DocRow key={d.id} d={d} onOpenDoc={onOpenDoc} />)}</div>
      )}
    </div>
  );
}

export function FinanceDocumentsSections({ documents, quotations, onOpenDoc }) {
  const groups = useMemo(() => {
    const g = { kyc: [], trade: [], ops: [], quotation: [] };
    for (const d of documents) g[classifyDoc(d)].push(d);
    return g;
  }, [documents]);
  const acceptedQuotation = (quotations || []).find((q) => statusKey(q.status) === "ACCEPTED");

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="fin-c360-documents">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Documents · {documents.length}</div>
      <DocGroup title="A. KYC / Customer Documents" icon={ShieldCheck} docs={groups.kyc} onOpenDoc={onOpenDoc} testid="fin-docs-kyc" />
      <DocGroup title="B. Trade / Shipment Documents" icon={FileText} docs={groups.trade} onOpenDoc={onOpenDoc} testid="fin-docs-trade" />
      <DocGroup title="C. Operations Documents" icon={Truck} docs={groups.ops} onOpenDoc={onOpenDoc} testid="fin-docs-ops" hint="Uploaded / shared by Operations — cost & booking evidence">
      </DocGroup>
      <DocGroup title="D. Quotation / Commercial Documents" icon={ScrollText} docs={groups.quotation} onOpenDoc={onOpenDoc} testid="fin-docs-quotation">
        {acceptedQuotation ? (
          <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[12px] text-emerald-800" data-testid="fin-accepted-quotation">
            <span className="font-semibold">Customer-accepted quotation:</span> {acceptedQuotation.id} · {acceptedQuotation.version} · {money(acceptedQuotation.sellTotal ?? acceptedQuotation.total, acceptedQuotation.currency)}
          </div>
        ) : (
          <div className="mb-2 text-[12px] text-slate-400">No quotation on this customer's record is marked Accepted yet.</div>
        )}
        {(quotations || []).length > 0 && (
          <div className="mb-2 space-y-1">
            {quotations.map((q) => (
              <div key={q.id} className="flex items-center justify-between text-[12px] text-slate-600" data-testid={`fin-quotation-${q.id}`}>
                <span className="font-mono-num">{q.id} · {q.version}</span>
                <span className="flex items-center gap-1.5"><span className="font-mono-num">{money(q.sellTotal ?? q.total, q.currency)}</span><StatusPill status={q.status} /></span>
              </div>
            ))}
          </div>
        )}
      </DocGroup>
    </div>
  );
}

// =========================== 2. FINAL CUSTOMER INVOICE =====================
export function FinanceFinalInvoice({ customerId, invoices, documents, onOpenDoc, onUpload, comms }) {
  const { data: rti } = useReadyToInvoice();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [approving, setApproving] = useState(false);

  const candidateJob = (rti || []).find((r) => r.customerId === customerId && r.invoiceEligible &&
    !(invoices || []).some((inv) => inv.jobId === r.jobId));

  const openDraft = async (jobId) => {
    setDrafting(jobId);
    try {
      const { data } = await api.post("/finance/final-invoice/draft", { jobId });
      setDraft({ ...data, total: Number(data.revenue ?? 0) });
    } catch (e) { toast.error(apiErrorMessage(e, "Final invoice draft could not be prepared.")); }
    finally { setDrafting(null); }
  };

  const approve = async () => {
    if (!draft) return;
    setApproving(true);
    try {
      await api.post("/finance/final-invoice/approve", { jobId: draft.jobId, total: Number(draft.total), lineItems: draft.lineItems });
      await Promise.all([qc.invalidateQueries({ queryKey: ["rti"] }), qc.invalidateQueries({ queryKey: ["customer", customerId] }), qc.invalidateQueries({ queryKey: ["ar"] })]);
      toast.success("Final invoice approved and issued");
      setDraft(null);
    } catch (e) { toast.error(apiErrorMessage(e, "Final invoice could not be issued.")); }
    finally { setApproving(false); }
  };

  const invoiceDoc = (inv) => documents.find((d) => (d.jobId && d.jobId === inv.jobId && (d.type === "Customer Invoice" || d.type === "Invoice")) || d.id === inv.documentId);

  const send = (inv) => {
    const doc = invoiceDoc(inv);
    comms("email", doc ? [{ id: doc.id, name: doc.name }] : undefined,
      `share final invoice ${inv.id} (${money(inv.amount, inv.currency)}, due ${inv.due || "—"})`);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="fin-c360-final-invoice">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Final Customer Invoice</div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onUpload} data-testid="fin-invoice-upload"><UploadCloud className="h-3 w-3 mr-1" />Upload invoice</Button>
          {candidateJob && (
            <Button size="sm" className="h-7 text-xs" disabled={drafting === candidateJob.jobId} onClick={() => openDraft(candidateJob.jobId)} data-testid="fin-invoice-create">
              {drafting === candidateJob.jobId ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FilePlus className="h-3 w-3 mr-1" />}Create invoice
            </Button>
          )}
        </div>
      </div>

      {(invoices || []).length === 0 ? (
        <div className="text-[12.5px] text-slate-400">No final invoice has been issued for this customer yet.{candidateJob ? " A job is ready for invoicing — use Create invoice above." : ""}</div>
      ) : (
        <div className="space-y-2.5">
          {invoices.map((inv) => {
            const outstanding = outstandingFor(inv);
            const doc = invoiceDoc(inv);
            return (
              <div key={inv.id} className="rounded-lg border border-slate-200 p-3" data-testid={`fin-invoice-${inv.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1.5 mb-2">
                  <span className="font-mono-num text-[13px] font-semibold text-slate-800">{inv.id} · Job {inv.jobId}</span>
                  <StatusPill status={inv.status} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-[12px]">
                  <MiniField label="Invoice date" value={inv.issued || "—"} />
                  <MiniField label="Due date" value={inv.due || "—"} />
                  <MiniField label="Amount" value={money(inv.amount, inv.currency || "AED")} />
                  <MiniField label="Amount paid" value={money(inv.paidAmount || 0, inv.currency || "AED")} />
                  <MiniField label="Outstanding" value={money(outstanding, inv.currency || "AED")} danger={outstanding > 0} />
                  <MiniField label="Payment status" value={inv.status} />
                </div>
                <div className="flex items-center justify-end gap-2 mt-2.5 pt-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!doc} onClick={() => doc && onOpenDoc(doc)} data-testid={`fin-invoice-preview-${inv.id}`}>
                    <Eye className="h-3 w-3 mr-1" />{doc ? "Preview" : "No document on file"}
                  </Button>
                  <Button size="sm" className="h-7 text-xs" onClick={() => send(inv)} data-testid={`fin-invoice-send-${inv.id}`}><Send className="h-3 w-3 mr-1" />Send to customer</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Tax/GST and a real "sent" timestamp/flag are not tracked anywhere in
          this system's invoice model — rather than fabricate them, they are
          simply not rendered. "Edit a draft before issuance" is honestly the
          same draft-review step above (adjust the invoice total, currently
          locked to the approved quotation per the existing Receivables.jsx
          behavior) — there is no separate persisted "draft" record to edit
          after the fact; a draft only exists in-memory until Approve. */}

      <Dialog open={!!draft} onOpenChange={(open) => { if (!open && !approving) setDraft(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="fin-final-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><FilePlus className="h-4 w-4 text-blue-800" />Final invoice draft</DialogTitle>
            <DialogDescription>Review before issuing — locked to the approved quotation total.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <Metric label="Quoted sell" value={money(draft.revenue, draft.currency || "AED")} />
                <Metric label="Actual cost" value={money(draft.actualCost, draft.currency || "AED")} muted />
                <Metric label="Margin" value={`${draft.marginPct}%`} accent />
              </div>
              <div>
                <label htmlFor="fin-final-invoice-total" className="text-xs font-semibold text-slate-700">Invoice total ({draft.currency || "AED"})</label>
                <Input id="fin-final-invoice-total" type="number" value={draft.total} readOnly aria-readonly="true" data-testid="fin-final-invoice-total" className="mt-1 bg-slate-50 font-mono-num" />
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDraft(null)} disabled={approving}>Cancel</Button>
                <Button onClick={approve} disabled={approving} data-testid="fin-final-invoice-approve">
                  {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Approve and issue
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const MiniField = ({ label, value, danger }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div><div className={`font-mono-num font-medium ${danger ? "text-red-600" : "text-slate-800"}`}>{value}</div></div>
);
const Metric = ({ label, value, accent, muted }) => <div className="rounded-md bg-slate-50 border border-slate-200 py-2"><div className={`font-display font-bold text-base font-mono-num ${accent ? "text-emerald-700" : muted ? "text-slate-500" : "text-slate-900"}`}>{value}</div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div></div>;

// ============================ 6. COSTS & VARIANCE ==========================
// Item 2 — each cost line shows its source documents (the original
// quotation Sales/the customer worked from, and the Operations-submitted
// invoice/cost-evidence document for that specific job) so Finance can
// compare them without leaving this page, and each variance line is
// approved/rejected SEPARATELY (never one blanket approval) via the same
// real /approvals/{id}/decision endpoint the Approvals page already uses —
// decidedBy/decidedAt/reason are already persisted by services/approvals.py
// decide(), just surfaced here per-line instead of only on /approvals.
export function FinanceCostsVariance({ jobs, approvals, documents, onOpenDoc }) {
  const { data: allSupplierInvoices } = useSupplierInvoices();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null);
  const [reasonDraft, setReasonDraft] = useState({}); // approvalId -> note text
  const jobIds = new Set((jobs || []).map((j) => j.id));
  const rows = (allSupplierInvoices || []).filter((s) => jobIds.has(s.jobId));

  // The single customer-accepted (or most recent) quotation document on
  // file — the same classification Section D (Quotation / Commercial
  // Documents) above already uses, reused here rather than re-fetched.
  const quotationDoc = (documents || []).find((d) => QUOTATION_TYPES.has(d.type));

  const invoiceDocFor = (row) => {
    const ids = row.documentIds || [];
    return (documents || []).find((d) => ids.includes(d.id));
  };

  const approvalFor = (sid) => (approvals || []).find((a) => a.objectType === "supplier_invoice" && a.objectId === sid);

  const requestApproval = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/finance/supplier-invoices/${row.id}/request-variance-approval`, { expectedVersion: Number(row.version ?? 0) });
      await Promise.all([qc.invalidateQueries({ queryKey: ["ap"] }), qc.invalidateQueries({ queryKey: ["customer"] })]);
      toast.success("Variance approval requested — a real APR record was created");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not request variance approval")); }
    finally { setBusy(null); }
  };

  const decide = async (appr, decision) => {
    setBusy(appr.id);
    try {
      await api.post(`/approvals/${appr.id}/decision`, { decision, reason: (reasonDraft[appr.id] || "").trim() || undefined, expectedVersion: Number(appr.version ?? 0) });
      await Promise.all([qc.invalidateQueries({ queryKey: ["ap"] }), qc.invalidateQueries({ queryKey: ["customer"] }), qc.invalidateQueries({ queryKey: ["approvals"] })]);
      toast.success(`Variance ${decision.toLowerCase()}`);
    } catch (e) { toast.error(apiErrorMessage(e, `Could not ${decision.toLowerCase()} the variance`)); }
    finally { setBusy(null); }
  };

  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="fin-c360-costs-variance">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Costs &amp; Variance</div>
      <div className="space-y-2">
        {rows.map((s) => {
          const variance = Number(s.amount || 0) - Number(s.expected || 0);
          const variancePct = s.expected ? Math.round((variance / Number(s.expected)) * 1000) / 10 : null;
          const isVariance = s.match === "Variance";
          const appr = approvalFor(s.id);
          const invoiceDoc = invoiceDocFor(s);
          const pending = appr && appr.status === "Pending";
          return (
            <div key={s.id} className={`rounded-lg border p-3 ${isVariance ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`} data-testid={`fin-variance-${s.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1.5">
                <span className="text-[13px] font-semibold text-slate-800">{s.vendor} · Job {s.jobId}</span>
                <StatusPill status={s.match} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 text-[12px]">
                <MiniField label="Charge / cost type" value={s.note || s.id} />
                <MiniField label="Supplier" value={s.vendor || "—"} />
                <MiniField label="Original quoted cost" value={money(s.expected, s.currency || "AED")} />
                <MiniField label="Actual cost" value={money(s.amount, s.currency || "AED")} />
                <MiniField label="Variance" value={`${money(variance, s.currency || "AED")}${variancePct == null ? "" : ` (${variancePct > 0 ? "+" : ""}${variancePct}%)`}`} danger={isVariance} />
              </div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!quotationDoc} onClick={() => quotationDoc && onOpenDoc(quotationDoc)} data-testid={`fin-variance-view-quotation-${s.id}`}>
                  <ScrollText className="h-3 w-3 mr-1" />{quotationDoc ? "View Quotation" : "No quotation on file"}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!invoiceDoc} onClick={() => invoiceDoc && onOpenDoc(invoiceDoc)} data-testid={`fin-variance-view-invoice-${s.id}`}>
                  <Truck className="h-3 w-3 mr-1" />{invoiceDoc ? "View Invoice" : "No Operations invoice on file"}
                </Button>
              </div>
              {isVariance && (
                <div className="mt-2.5 pt-2 border-t border-amber-100">
                  {appr && !pending ? (
                    <div className="flex items-center gap-2 text-[12px]" data-testid={`fin-variance-decided-${s.id}`}>
                      <Gavel className="h-3.5 w-3.5 text-amber-600" />
                      <span className="text-slate-600">Variance approval <span className="font-mono-num">{appr.id}</span>:</span>
                      <StatusPill status={appr.status} />
                      {appr.decidedBy && <span className="text-slate-400">by {appr.decidedBy} · {appr.decidedAt ? new Date(appr.decidedAt).toLocaleString() : "—"}{appr.decisionReason ? ` · ${appr.decisionReason}` : ""}</span>}
                    </div>
                  ) : pending ? (
                    <div className="space-y-1.5" data-testid={`fin-variance-pending-${s.id}`}>
                      <div className="flex items-center gap-2 text-[12px]">
                        <Gavel className="h-3.5 w-3.5 text-amber-600" />
                        <span className="text-slate-600">Variance approval <span className="font-mono-num">{appr.id}</span>:</span>
                        <StatusPill status="Approval Pending" />
                      </div>
                      <Input value={reasonDraft[appr.id] || ""} onChange={(e) => setReasonDraft((cur) => ({ ...cur, [appr.id]: e.target.value }))}
                        placeholder="Notes / reason (required to reject or query)" className="h-7 text-xs" data-testid={`fin-variance-reason-${s.id}`} />
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={busy === appr.id} onClick={() => decide(appr, "Approved")} data-testid={`fin-variance-approve-${s.id}`}>
                          {busy === appr.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}Approve Variance
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === appr.id || !(reasonDraft[appr.id] || "").trim()} onClick={() => decide(appr, "Rejected")} data-testid={`fin-variance-reject-${s.id}`}>
                          <AlertTriangle className="h-3 w-3 mr-1" />Reject / Query
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === s.id} onClick={() => requestApproval(s)} data-testid={`fin-variance-request-${s.id}`}>
                      {busy === s.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <AlertTriangle className="h-3 w-3 mr-1" />}Request variance approval
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ========================== 8. PAYMENT & OUTSTANDING =======================
export function FinancePaymentOutstanding({ invoices, comms, customer }) {
  const qc = useQueryClient();
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [payment, setPayment] = useState({ amount: "", reference: "", receivedAt: today() });
  const [busy, setBusy] = useState(false);
  // Item 3 — "Ask Customer" reuses the exact same comms() function every
  // other communication action on this page already uses (opens the real
  // Composer against the customer's stored contact, resolved server-side
  // by customerId — never re-entered here). This just toggles which
  // invoice's channel-choice row is shown; no new messaging mechanism.
  const [askInvoiceId, setAskInvoiceId] = useState(null);
  const askCustomer = (inv, channel) => {
    setAskInvoiceId(null);
    comms(channel, undefined, `follow up with ${customer?.name || "the customer"} about the outstanding ${money(outstandingFor(inv), inv.currency || "AED")} on invoice ${inv.id}`);
  };

  const openPayment = (inv) => { setPaymentInvoice(inv); setPayment({ amount: String(outstandingFor(inv)), reference: "", receivedAt: today() }); };

  const recordPayment = async (e) => {
    e.preventDefault();
    const amount = Number(payment.amount);
    if (!paymentInvoice || !Number.isFinite(amount) || amount <= 0 || !payment.reference.trim() || !payment.receivedAt) return;
    setBusy(true);
    try {
      await api.post(`/finance/customer-invoices/${paymentInvoice.id}/payment`, {
        amount, reference: payment.reference.trim(), receivedAt: payment.receivedAt, expectedVersion: Number(paymentInvoice.version ?? 0),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ["ar"] }), qc.invalidateQueries({ queryKey: ["customer"] }), qc.invalidateQueries({ queryKey: ["profit"] })]);
      toast.success("Customer payment recorded");
      setPaymentInvoice(null);
    } catch (e2) { toast.error(apiErrorMessage(e2, "Customer payment could not be recorded.")); }
    finally { setBusy(false); }
  };

  const openInvoices = (invoices || []).filter((inv) => ["OPEN", "OVERDUE", "PARTIALLY PAID"].includes(statusKey(inv.status)));
  if (openInvoices.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="fin-c360-payment-outstanding">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Payment &amp; Outstanding</div>
      <div className="space-y-2.5">
        {openInvoices.map((inv) => {
          const overdue = statusKey(inv.status) === "OVERDUE";
          const outstanding = outstandingFor(inv);
          return (
            <div key={inv.id} className={`rounded-lg border p-3 ${overdue ? "border-red-200 bg-red-50/30" : "border-slate-200"}`} data-testid={`fin-payment-${inv.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1.5 mb-2">
                <span className="font-mono-num text-[13px] font-semibold text-slate-800">{inv.id} · Job {inv.jobId}</span>
                <StatusPill status={inv.status} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-[12px]">
                <MiniField label="Invoice date" value={inv.issued || "—"} />
                <MiniField label="Amount" value={money(inv.amount, inv.currency || "AED")} />
                <MiniField label="Amount paid" value={money(inv.paidAmount || 0, inv.currency || "AED")} />
                <MiniField label="Outstanding" value={money(outstanding, inv.currency || "AED")} danger />
                <MiniField label="Due date" value={inv.due || "—"} />
                <MiniField label="Aging" value={inv.aging || "—"} danger={overdue} />
              </div>
              <div className="flex items-center justify-end gap-2 mt-2.5 pt-2 border-t border-slate-100 flex-wrap">
                {overdue && (
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => comms("whatsapp", undefined, "send a polite overdue payment reminder")} data-testid={`fin-payment-remind-${inv.id}`}>
                    <MessageCircle className="h-3 w-3 mr-1" />Send reminder
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAskInvoiceId((cur) => (cur === inv.id ? null : inv.id))} data-testid={`fin-payment-ask-${inv.id}`}>
                  <MessageCircle className="h-3 w-3 mr-1" />Ask Customer
                </Button>
                <Button size="sm" className="h-7 text-xs" onClick={() => openPayment(inv)} data-testid={`fin-payment-record-${inv.id}`}><Banknote className="h-3 w-3 mr-1" />Record payment</Button>
              </div>
              {askInvoiceId === inv.id && (
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-slate-100" data-testid={`fin-payment-ask-choices-${inv.id}`}>
                  <span className="text-[11px] text-slate-400 mr-1">Ask via:</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => askCustomer(inv, "whatsapp")} data-testid={`fin-payment-ask-whatsapp-${inv.id}`}><MessageCircle className="h-3 w-3 mr-1" />WhatsApp</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => askCustomer(inv, "email")} data-testid={`fin-payment-ask-email-${inv.id}`}><Mail className="h-3 w-3 mr-1" />Email</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => askCustomer(inv, "call")} data-testid={`fin-payment-ask-call-${inv.id}`}><Phone className="h-3 w-3 mr-1" />Call</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Last/next follow-up date is not tracked by any backend field —
          intentionally not rendered rather than fabricated (same honesty
          note as the earlier Pipeline.jsx round that built this data path). */}

      <Dialog open={!!paymentInvoice} onOpenChange={(open) => { if (!open && !busy) setPaymentInvoice(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="fin-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Banknote className="h-4 w-4 text-blue-800" />Record customer payment</DialogTitle>
            <DialogDescription>{paymentInvoice ? `${paymentInvoice.id} · Job ${paymentInvoice.jobId}` : ""}</DialogDescription>
          </DialogHeader>
          {paymentInvoice && (
            <form className="space-y-3" onSubmit={recordPayment}>
              <div>
                <label htmlFor="fin-payment-amount" className="text-xs font-semibold text-slate-700">Amount received ({paymentInvoice.currency || "AED"})</label>
                <Input id="fin-payment-amount" type="number" min="0.01" step="0.01" max={outstandingFor(paymentInvoice)} value={payment.amount} onChange={(e) => setPayment((v) => ({ ...v, amount: e.target.value }))} className="mt-1 font-mono-num" required autoFocus />
              </div>
              <div>
                <label htmlFor="fin-payment-reference" className="text-xs font-semibold text-slate-700">Payment reference</label>
                <Input id="fin-payment-reference" value={payment.reference} onChange={(e) => setPayment((v) => ({ ...v, reference: e.target.value }))} placeholder="Bank transfer or receipt reference" className="mt-1" required />
              </div>
              <div>
                <label htmlFor="fin-payment-date" className="text-xs font-semibold text-slate-700">Received date</label>
                <Input id="fin-payment-date" type="date" value={payment.receivedAt} onChange={(e) => setPayment((v) => ({ ...v, receivedAt: e.target.value }))} className="mt-1" required />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setPaymentInvoice(null)} disabled={busy}>Cancel</Button>
                <Button type="submit" disabled={busy || Number(payment.amount) <= 0 || !payment.reference.trim() || !payment.receivedAt} data-testid="fin-payment-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}Record payment
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
